import { Moment } from 'moment';
import { VehicleDto } from '../assets';
import { DriverDto } from '../tenants/driver.dto';

export type DashcamProviderType = 'turbohive' | 'visionmax';

export interface CameraModelDto {
  code: string;
  name: string;
  deviceType: string;
  protocol: string;
}

export interface CameraVendorDto {
  code: string;
  name: string;
}

export interface DashcamChannelDto {
  role: string;
  label: string;
  number: number;
  enabled: boolean;
  available: boolean;
  providerMetadata: {
    provider: string;
    capture_mode: string;
  };
  recordingCatalog: {
    scannedThrough: string;
  };
}

export type ProviderTimestampMode = 'absolute' | 'camera_wall_clock';

export interface DashcamDto {
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  id: number;
  companyId: string;
  truckId: string;
  imei: string;
  manufacturer: string;
  model: string;
  providerType: DashcamProviderType;
  providerTimestampMode: ProviderTimestampMode;
  displayName: string;
  status: string;
  isActive: boolean;
  channels: DashcamChannelDto[];
  truck?: VehicleDto;
}

export interface CreateDashcamModel {
  imei: string;
  manufacturer: string;
  model: string;
  providerType: string;
  displayName: string;
  providerTimestampMode: string;
  truckId: string;
  companyId: string;
  isActive: boolean;
  channels: AddChannelModel[];
}

export interface EditDashcamModel {
  displayName: string;
  providerTimestampMode: string;
  isActive: boolean;
  truck?: VehicleDto;
  channels?: AddChannelModel[];
}

export interface AddChannelModel {
  number: number;
  role: string;
  label: string;
  enabled: boolean;
}

export interface CreateLiveStreamModel {
  channel: number;
  dataType: 'audio_video';
  streamType: 'main_stream';
}

export type LiveStreamStatus = 'starting' | 'active' | 'stale' | 'stopping' | 'stopped' | 'expired' | 'failed';

export interface LiveStreamSessionModel {
  id: number;
  status: LiveStreamStatus;
  channel: {
    number: number;
  };
  effective: {
    dataType: 'audio_video';
    streamType: 'main_stream';
  };
  camera: {
    id: number;
    displayName: string;
    imei: string;
  };
  /* vehicle: {
    id: string | null;
    label: string | null;
  }; */
  vehicle: {
    id: string | null;
    vehicleId: string | null;
  };
  driver: {
    id: string | null;
    name: string | null;
  };
  drivers: {
    id: string | null;
    fullName: string | null;
  }[];
  provider: {
    type: string;
    source: string;
    sessionId: string;
    liveSessionId: number;
  };
  transport: 'hls';
  streamUrls: {
    ssl_hls: string;
  };
  webrtc: null;
  viewerPolicy: {
    keepaliveIntervalSeconds: number;
    timeoutSeconds: number;
    sharingMode: 'gateway_relay';
  };
  viewers: {
    active: number;
  };
  viewer: {
    id: number;
    token: string;
    status: string;
    requested: {
      channel: number;
      dataType: 'audio_video';
      streamType: 'main_stream';
    };
    effective: {
      channel: number;
      dataType: 'audio_video';
      streamType: 'main_stream';
    };
    exactParamsMatch: boolean;
    lastSeenAt: string;
    expiresAt: string;
    releasedAt: string | null;
  };
  lastViewerSeenAt: string;
  lastProviderStartedAt: string;
  lastProviderStoppedAt: string | null;
  lastHealthCheckedAt: string | null;
  lastMediaSeenAt: string | null;
  stoppedAt: string | null;
  errorMessage: string | null;
}

export interface PlaybackSessionModel {
  id: number;
  status: LiveStreamStatus;
  sourceType: string;
  channel: {
    number: number;
  };
  camera: {
    id: number;
    displayName: string;
    imei: string;
    timezone: string;
    effectiveTimezone: string;
    timezoneSource: string;
  };
  /* vehicle: {
    id: string | null;
    label: string | null;
  }; */
  vehicle: {
    id: string | null;
    vehicleId: string | null;
  };
  driver: {
    id: string | null;
    name: string | null;
  };
  drivers: {
    id: string | null;
    fullName: string | null;
  }[];
  event: {
    id: number | null;
    type: string | null;
    label: string | null;
    severity: string | null;
  };
  provider: {
    type: DashcamProviderType;
    source: string;
    sessionId: string;
    fileListCommandId: number | null;
    timestampMode: string;
  };
  playbackWindow: {
    from: string;
    to: string;
  };
  fileNames: string[];
  fileDiscovery: {
    source: string;
    label: string;
    fileIds: number[];
    fileListCommandId: number | null;
    fileCount: number;
    diagnostics: {
      source: string;
      coversWindow: boolean;
      reason: string;
      fileCount: number;
      maxFiles: number;
      requestedFrom: string;
      requestedTo: string;
      coverageFrom: string | null;
      coverageTo: string | null;
      allowedGapSeconds: number;
      queryLimitHit: boolean;
    };
  };
  transport: string;
  streamUrls: {
    ssl_hls: string;
  };
  webrtc: string | null;
  requestedAt: string;
  fileListReceivedAt: string | null;
  startedAt: string | null;
  stoppedAt: string | null;
  expiresAt: string;
  errorMessage: string | null;
}

export interface CreatePlaybackSessionModel {
  channel: number;
  from: string;
  to: string;
}

// NOTE | SAFETY EVENTS

export type SafetyEventStatus = 'requested' | 'processing' | 'ready' | 'partial' | 'failed' | 'expired';

export interface SafetyEventLocation {
  latitude: number;
  longitude: number;
  address: string | null;
}

export interface SafetyEventModel {
  id: number;
  eventType: string;
  label: string;
  severity: string;
  status: string;
  camera: {
    id: number;
    displayName: string;
    imei: string;
    timezone: string;
    effectiveTimezone: string;
    timezoneSource: string;
    providerUtcOffsetMinutes: string | null;
  };
  vehicle: {
    id: string | null;
    label: string | null;
  };
  driver: {
    id: string | null;
    name: string | null;
  };
  provider: {
    eventId: string;
    code: string;
    type: string;
    source: DashcamProviderType;
  };
  occurredAt: string;
  occurredAtCamera: string;
  endedAt: string | null;
  endedAtCamera: string | null;
  durationSeconds: number | null;
  triggerType: 'Event';
  firingStatus: string | null;
  location: SafetyEventLocation;
  speedKph: number | null;
  speedLimitKph: number | null;
  headingDegrees: number | null;
  mediaStatus: SafetyEventStatus;
  summary: number | null;
}

export interface SafetyEventModelV2 {
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  id: string;
  companyId: string;
  cameraId: number;
  truckId: string | null;
  truck: VehicleDto | null;
  driverId1: string | null;
  driverId2: string | null;
  driver1?: DriverDto | null;
  driver2?: DriverDto | null;
  eventType: string;
  eventClass: string;
  label: string;
  severity: string;
  status: string;
  imei: string;
  vehicleId: string | null;
  lat: number | null;
  lon: number | null;
  headingDegrees: number | null;
  speedLimitKph: number | null;
  occurredAt: string;
  providerType: DashcamProviderType;
  providerEventId: string;
  providerCode: string;
}

export type SafetyEventNeighborModel = Pick<SafetyEventModelV2, 'id' | 'occurredAt' | 'eventType' | 'label'>;

export interface SafetyEventNeighborsModel {
  previousEvent: SafetyEventNeighborModel | null;
  nextEvent: SafetyEventNeighborModel | null;
}

export interface SafetyEventVideoBundle {
  id: number;
  status: SafetyEventStatus;
  event: {
    id: number;
    type: string;
    label: string;
    severity: string;
    occurredAt: string;
    endedAt: string | null;
  };
  camera: {
    id: number;
    displayName: string;
    imei: string;
    timezone: string;
    effectiveTimezone: string;
    timezoneSource: string;
  };
  vehicle: {
    id: string | null;
    label: string | null;
  };
  driver: {
    id: string | null;
    name: string | null;
  };
  provider: {
    id: number;
    type: DashcamProviderType;
    source: string;
  };
  videoWindow: {
    from: string;
    to: string;
    durationSeconds: number;
    beforeSeconds: number;
    afterSeconds: number;
  };
  channels: number[];
  clips: SafetyEventClip[];
  metadata: {
    source: string;
    synchronization: string;
    readyClips: number;
    failedClips: number;
    processingClips: number;
  };
  requestedAt: string;
  processingStartedAt: string;
  completedAt: string;
  expiresAt: string | null;
  errorMessage: string | null;
}

export interface SafetyEventClip {
  id: number;
  status: SafetyEventStatus;
  channel: {
    number: number;
  };
  channels: number[];
  layout: string;
  playbackSessionId: number | null;
  url: string | null;
  downloadUrl: string | null;
  fileName: string;
  mimeType: string;
  fileSizeBytes: number | null;
  source: {
    fileNames: string[];
    streamUrls: {
      provider_mp4: string;
    };
  };
  recordingWindow: {
    from: string;
    to: string;
  };
  clipWindow: {
    from: string;
    to: string;
    offsetSeconds: number;
    durationSeconds: number;
  };
  metadata: {
    provider: DashcamProviderType;
    captureMode: string;
    coveredChannels: number[];
    source: string;
    taskId: number | null;
  };
  errorMessage: string | null;
  updatedAt: string;
}

export interface CreateEventVideoBundleModel {
  channels: number[];
  beforeSeconds: number;
  afterSeconds: number;
}

// NOTE | MEDIA MODELS

export interface SafetyMediaModel {
  id: number;
  mediaType: string;
  status: string;
  fileName: string | null;
  mimeType: string;
  fileSizeBytes: number | null;
  captureTime: string;
  expiresAt: string | null;
  channel: {
    number: number | null;
    role: string | null;
  };
  camera: {
    id: number;
    displayName: string;
    imei: string;
  };
  vehicle: {
    id: number | null;
    label: string | null;
  };
  driver: {
    id: number | null;
    name: string | null;
  };
  event: {
    id: number | null;
    type: string;
    label: string | null;
    severity: string | null;
    occurredAt: string | null;
  };
  provider: {
    type: string;
    source: string;
    mediaId: string;
    url: string;
  };
  download: {
    isStored: boolean;
  };
}

export interface CameraParamsModel {
  latitude: number | null;
  longitude: number | null;
  speedKph: number | null;
  headingDegrees: number | null;
  altitudeMeters: number | null;
  recordedAt: string;
}

export interface CameraMoreParamsModel extends CameraParamsModel {
  date: Moment;
  direction: number;
}
